import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  filaPizzasService,
  AssignedItem,
  CancelledItemAlert,
  MeuStatusResponse,
} from "../../../services/filaPizzasService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../../../context/AuthContext";
import { useBordeiroEvents } from "../../../hooks/useBordeiroEvents";
import { bordaService, Borda } from "../../../services/bordaService";
import { tamanhoService, Tamanho } from "../../../services/tamanhoService";
import { COLORS } from "../../../constants";

interface PizzaioloIntegracaoContextType {
  isOnline: boolean;
  isFilaSalgada: boolean;
  isFilaDoce: boolean;
  isFilaSalaoSalgada: boolean;
  isFilaSalaoDoce: boolean;
  isFilaRodizioSalgada: boolean;
  isFilaRodizioDoce: boolean;
  currentItem: AssignedItem | null;
  timeElapsed: number;
  isFinalizing: boolean;
  isPulando: boolean;
  isTogglingOnline: boolean;
  isLoadingStatus: boolean;
  pendingSalgada: number;
  pendingDoce: number;
  pendingSalaoSalgada: number;
  pendingSalaoDoce: number;
  isRodizioModalOpen: boolean;
  isSalaoModalOpen: boolean;
  tamanhosRodizio: Tamanho[];
  tamanhosSalao: Tamanho[];
  bordasSalao: Borda[];
  isLoadingTamanhosRodizio: boolean;
  isLoadingSalaoOptions: boolean;
  isCreatingRodizio: boolean;
  isRodizioSalgadoTimerActive: boolean;
  rodizioSalgadoLabel: string;
  rodizioSalgadoTimeElapsed: number;
  isFinalizingRodizioSalgado: boolean;
  isSalaoTimerActive: boolean;
  salaoLabel: string;
  salaoTimeElapsed: number;
  isFinalizingSalao: boolean;
  handleToggleOnline: () => void;
  handleFinalizarProximo: () => void;
  handleFinalizarOffline: () => void;
  openRodizioModal: () => void;
  closeRodizioModal: () => void;
  openSalaoModal: () => void;
  closeSalaoModal: () => void;
  handleCriarRodizio: (tamanhoId: number, quantidade: number) => void;
  handleStartRodizioSalgadoTimer: (
    tamanhoId: number,
    tamanhoNome: string
  ) => void;
  handleFinalizarRodizioSalgadoTimer: () => void;
  handleStartSalaoTimer: (
    tamanhoId: number,
    tamanhoNome: string,
    bordaId: number
  ) => void;
  handleFinalizarSalaoTimer: () => void;
  handlePularItem: (motivo: string) => void;
  handleDefinirTamanhoRodizio: (tamanho: "08" | "broto") => void;
  isDefinindoTamanhoRodizio: boolean;
}

const PizzaioloIntegracaoContext = createContext<
  PizzaioloIntegracaoContextType | undefined
>(undefined);

const CANCELLED_ACKS_STORAGE_KEY = "pizzaioloIntegracaoCancelledAcks";
const MAX_CANCELLED_ACKS = 200;

export const PizzaioloIntegracaoProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isOnline, setIsOnline] = useState(false);
  const [isFilaSalgada, setIsFilaSalgada] = useState(false);
  const [isFilaDoce, setIsFilaDoce] = useState(false);
  const [isFilaSalaoSalgada, setIsFilaSalaoSalgada] = useState(false);
  const [isFilaSalaoDoce, setIsFilaSalaoDoce] = useState(false);
  const [isFilaRodizioSalgada, setIsFilaRodizioSalgada] = useState(false);
  const [isFilaRodizioDoce, setIsFilaRodizioDoce] = useState(false);
  const [currentItem, setCurrentItem] = useState<AssignedItem | null>(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [pendingSalgada, setPendingSalgada] = useState(0);
  const [pendingDoce, setPendingDoce] = useState(0);
  const [pendingSalaoSalgada, setPendingSalaoSalgada] = useState(0);
  const [pendingSalaoDoce, setPendingSalaoDoce] = useState(0);
  const [isRodizioModalOpen, setIsRodizioModalOpen] = useState(false);
  const [isSalaoModalOpen, setIsSalaoModalOpen] = useState(false);
  const [isRodizioSalgadoTimerActive, setIsRodizioSalgadoTimerActive] =
    useState(false);
  const [rodizioSalgadoTamanhoId, setRodizioSalgadoTamanhoId] = useState<
    number | null
  >(null);
  const [rodizioSalgadoLabel, setRodizioSalgadoLabel] = useState("");
  const [rodizioSalgadoTimeElapsed, setRodizioSalgadoTimeElapsed] = useState(0);
  const [isSalaoTimerActive, setIsSalaoTimerActive] = useState(false);
  const [salaoTamanhoId, setSalaoTamanhoId] = useState<number | null>(null);
  const [salaoBordaId, setSalaoBordaId] = useState<number | null>(null);
  const [salaoLabel, setSalaoLabel] = useState("");
  const [salaoTimeElapsed, setSalaoTimeElapsed] = useState(0);
  const [sseAvailable, setSseAvailable] = useState(true);
  const [cancelledQueue, setCancelledQueue] = useState<CancelledItemAlert[]>(
    []
  );
  const currentItemIdRef = useRef<number | null>(null);
  const hasActiveItemRef = useRef(false);
  const acknowledgedCancelledRef = useRef<Set<string>>(new Set());
  const showingCancelledAlertRef = useRef(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const refreshStatusDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    hasActiveItemRef.current = currentItem != null;
  }, [currentItem]);

  const { data: tamanhosDaLoja = [], isLoading: isLoadingTamanhosRodizio } =
    useQuery({
      queryKey: ["tamanhos-rodizio", user?.lojaId],
      queryFn: () => tamanhoService.getTamanhos({ lojaId: user!.lojaId }),
      enabled: (isRodizioModalOpen || isSalaoModalOpen) && !!user?.lojaId,
    });

  const tamanhosRodizio = tamanhosDaLoja.filter((tamanho) =>
    tamanho.nome.toLowerCase().includes("rodizio")
  );
  const tamanhosSalao = tamanhosDaLoja.filter((tamanho) =>
    tamanho.nome
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .includes("salao")
  );

  const { data: bordasSalao = [], isLoading: isLoadingBordasSalao } = useQuery({
    queryKey: ["bordas-salao", user?.lojaId],
    queryFn: () => bordaService.getBordas({ lojaId: user!.lojaId }),
    enabled: isSalaoModalOpen && !!user?.lojaId,
  });

  const persistAcknowledgedCancelled = useCallback(() => {
    try {
      const values = Array.from(acknowledgedCancelledRef.current);
      const trimmed = values.slice(-MAX_CANCELLED_ACKS);
      localStorage.setItem(CANCELLED_ACKS_STORAGE_KEY, JSON.stringify(trimmed));
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CANCELLED_ACKS_STORAGE_KEY);
      if (!saved) return;

      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return;

      const sanitized = parsed.filter(
        (item): item is string => typeof item === "string" && item.length > 0
      );
      acknowledgedCancelledRef.current = new Set(
        sanitized.slice(-MAX_CANCELLED_ACKS)
      );
    } catch {
      acknowledgedCancelledRef.current = new Set();
    }
  }, []);

  const startNewItem = useCallback((item: AssignedItem) => {
    setCurrentItem(item);
    setTimeElapsed(0);
    currentItemIdRef.current = item.id;
  }, []);

  const clearItem = useCallback(() => {
    setCurrentItem(null);
    setTimeElapsed(0);
    currentItemIdRef.current = null;
  }, []);

  const applyStatus = useCallback(
    (status: MeuStatusResponse) => {
      setIsOnline(status.isOnline);
      setIsFilaSalgada(status.isFilaSalgada);
      setIsFilaDoce(status.isFilaDoce);
      setIsFilaSalaoSalgada(status.isFilaSalaoSalgada ?? false);
      setIsFilaSalaoDoce(status.isFilaSalaoDoce ?? false);
      setIsFilaRodizioSalgada(status.isFilaRodizioSalgada ?? false);
      setIsFilaRodizioDoce(status.isFilaRodizioDoce ?? false);
      setPendingSalgada(status.pendingSalgada);
      setPendingDoce(status.pendingDoce);
      setPendingSalaoSalgada(status.pendingSalaoSalgada ?? 0);
      setPendingSalaoDoce(status.pendingSalaoDoce ?? 0);

      if (status.currentItem) {
        const incoming = status.currentItem;
        if (currentItemIdRef.current !== incoming.id) {
          startNewItem(incoming);
        } else {
          // Mesmo item: atualiza dados (ex.: observação incluída no painel) sem zerar o cronômetro.
          setCurrentItem((prev) =>
            prev && JSON.stringify(prev) === JSON.stringify(incoming)
              ? prev
              : incoming
          );
        }
      } else {
        clearItem();
      }

      const incomingCancelled = status.cancelledItems || [];
      if (incomingCancelled.length > 0) {
        setCancelledQueue((prev) => {
          const existingKeys = new Set(
            prev.map((item) => `${item.tipo}-${item.id}`)
          );
          const merged = [...prev];

          incomingCancelled.forEach((item) => {
            const key = `${item.tipo}-${item.id}`;
            if (
              !acknowledgedCancelledRef.current.has(key) &&
              !existingKeys.has(key)
            ) {
              merged.push(item);
            }
          });

          return merged;
        });
      }
    },
    [startNewItem, clearItem]
  );

  const scheduleMeuStatusRefresh = useCallback(() => {
    if (refreshStatusDebounceRef.current != null) {
      clearTimeout(refreshStatusDebounceRef.current);
    }
    // Coalesce bursts de SSE em 1 refresh (tempo real sem tempestade HTTP).
    refreshStatusDebounceRef.current = setTimeout(() => {
      refreshStatusDebounceRef.current = null;
      void filaPizzasService
        .getMeuStatus({ tryAssign: !hasActiveItemRef.current })
        .then(applyStatus)
        .catch(() => {});
    }, 1_000);
  }, [applyStatus]);

  const syncMeuStatusAfterWorkflow = useCallback(
    async (nextItem: AssignedItem | null) => {
      if (nextItem) {
        startNewItem(nextItem);
      } else {
        clearItem();
      }

      try {
        const status = await filaPizzasService.getMeuStatus({
          tryAssign: nextItem == null,
        });
        applyStatus(status);
      } catch {}
    },
    [startNewItem, clearItem, applyStatus]
  );

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await filaPizzasService.getMeuStatus({
          tryAssign: true,
        });
        applyStatus(status);
      } catch {
      } finally {
        setIsLoadingStatus(false);
      }
    };
    loadStatus();
  }, [applyStatus]);

  useEffect(() => {
    if (!isOnline) return;

    // Tempo real via SSE; poll só heartbeat (SSE ok) ou fallback (SSE caído).
    const pollMs = sseAvailable ? 30_000 : currentItem ? 12_000 : 8_000;
    const interval = setInterval(() => {
      void filaPizzasService
        .getMeuStatus({ tryAssign: !hasActiveItemRef.current })
        .then(applyStatus)
        .catch(() => {});
    }, pollMs);

    return () => clearInterval(interval);
  }, [isOnline, currentItem, applyStatus, sseAvailable]);

  useEffect(() => {
    if (!isOnline) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        scheduleMeuStatusRefresh();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isOnline, scheduleMeuStatusRefresh]);

  useBordeiroEvents(scheduleMeuStatusRefresh, user?.lojaId ?? null, undefined, {
    enabled: isOnline,
    onAvailabilityChange: setSseAvailable,
  });

  useEffect(() => {
    return () => {
      if (refreshStatusDebounceRef.current != null) {
        clearTimeout(refreshStatusDebounceRef.current);
        refreshStatusDebounceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!currentItem) return;

    const interval = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentItem]);

  useEffect(() => {
    if (showingCancelledAlertRef.current || cancelledQueue.length === 0) return;

    const nextCancelled = cancelledQueue[0];
    if (!nextCancelled) return;

    showingCancelledAlertRef.current = true;

    void Swal.fire({
      title: "Pedido cancelado",
      text: `Pedido #${nextCancelled.displayId || nextCancelled.orderId} foi cancelado e removido da produção.`,
      icon: "warning",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: true,
    }).then(() => {
      const key = `${nextCancelled.tipo}-${nextCancelled.id}`;
      acknowledgedCancelledRef.current.add(key);
      persistAcknowledgedCancelled();
      setCancelledQueue((prev) =>
        prev.filter((item) => `${item.tipo}-${item.id}` !== key)
      );
      showingCancelledAlertRef.current = false;
    });
  }, [cancelledQueue, persistAcknowledgedCancelled]);

  const toggleOnlineMutation = useMutation({
    mutationFn: async () => {
      if (isOnline) {
        return filaPizzasService.ficarOffline();
      } else {
        return filaPizzasService.ficarOnline();
      }
    },
    onSuccess: (status) => {
      applyStatus(status);
      if (status.isOnline) {
        toast.success("Você está ativo nas filas! Aguardando pedidos...");
      } else {
        toast.info("Você ficou inativo nas filas.");
      }
    },
    onError: (error: any) => {
      Swal.fire("Erro", error?.message || "Erro ao alterar status", "error");
    },
  });

  const finalizarProximoMutation = useMutation({
    mutationFn: async () => {
      if (!currentItem) throw new Error("Nenhum item ativo");
      return filaPizzasService.finalizarProducaoIntegracao(
        currentItem.id,
        currentItem.tipo,
        timeElapsed
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["fila-salgada"] });
      queryClient.invalidateQueries({ queryKey: ["fila-doce"] });
      toast.success("Pizza finalizada com sucesso!");
      void syncMeuStatusAfterWorkflow(result.nextItem);
    },
    onError: (error: any) => {
      Swal.fire("Erro", error?.message || "Erro ao finalizar pizza", "error");
    },
  });

  const finalizarOfflineMutation = useMutation({
    mutationFn: async () => {
      if (!currentItem) throw new Error("Nenhum item ativo");
      await filaPizzasService.finalizarProducaoIntegracao(
        currentItem.id,
        currentItem.tipo,
        timeElapsed
      );
      return filaPizzasService.ficarOffline();
    },
    onSuccess: (status) => {
      queryClient.invalidateQueries({ queryKey: ["fila-salgada"] });
      queryClient.invalidateQueries({ queryKey: ["fila-doce"] });
      toast.info("Pizza finalizada! Você ficou offline.");
      clearItem();
      applyStatus(status);
    },
    onError: (error: any) => {
      Swal.fire("Erro", error?.message || "Erro ao finalizar pizza", "error");
    },
  });

  const pularItemMutation = useMutation({
    mutationFn: async (motivo: string) => {
      if (!currentItem) throw new Error("Nenhum item ativo");
      return filaPizzasService.pularItem(
        currentItem.id,
        currentItem.tipo,
        motivo
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["fila-salgada"] });
      queryClient.invalidateQueries({ queryKey: ["fila-doce"] });
      toast.info("Pedido pulado. Bordeiro foi avisado da borda em falta.");
      void syncMeuStatusAfterWorkflow(result.nextItem);
    },
    onError: (error: any) => {
      Swal.fire("Erro", error?.message || "Erro ao pular pedido", "error");
    },
  });

  const definirTamanhoRodizioMutation = useMutation({
    mutationFn: async (tamanho: "08" | "broto") => {
      if (!currentItem) throw new Error("Nenhum item ativo");
      if (
        currentItem.tipo !== "rodizio_salgada" &&
        currentItem.tipo !== "rodizio_doce"
      ) {
        throw new Error("Item atual não é de rodízio.");
      }
      return filaPizzasService.definirTamanhoRodizioItem(
        currentItem.id,
        currentItem.tipo,
        tamanho
      );
    },
    onSuccess: (updated) => {
      setCurrentItem(updated);
      toast.success(`Tamanho definido: ${updated.descricao?.[0]?.name || ""}`);
    },
    onError: (error: any) => {
      Swal.fire(
        "Erro",
        error?.message || "Erro ao definir tamanho do rodízio",
        "error"
      );
    },
  });

  const criarRodizioMutation = useMutation({
    mutationFn: async (payload: { tamanhoId: number; quantidade: number }) => {
      return filaPizzasService.criarRodizio(payload);
    },
    onSuccess: (result) => {
      toast.success(
        `${result.createdCount} pizza(s) de rodízio registrada(s) com sucesso.`
      );
      queryClient.invalidateQueries({ queryKey: ["ranking-dia-producao"] });
      queryClient.invalidateQueries({ queryKey: ["producoes"] });
      queryClient.invalidateQueries({ queryKey: ["producao-resumo"] });
      queryClient.invalidateQueries({ queryKey: ["producao-usuarios"] });
      queryClient.invalidateQueries({ queryKey: ["usuarios-producao"] });
      setIsRodizioModalOpen(false);
    },
    onError: (error: any) => {
      Swal.fire(
        "Erro",
        error?.message || "Erro ao registrar pizzas de rodízio",
        "error"
      );
    },
  });

  const finalizarRodizioSalgadoMutation = useMutation({
    mutationFn: async (payload: { tamanhoId: number; tempoTotal: number }) => {
      return filaPizzasService.finalizarRodizioSalgado(payload);
    },
    onSuccess: () => {
      toast.success("Rodízio salgado finalizado e contabilizado com sucesso.");
      queryClient.invalidateQueries({ queryKey: ["ranking-dia-producao"] });
      queryClient.invalidateQueries({ queryKey: ["producoes"] });
      queryClient.invalidateQueries({ queryKey: ["producao-resumo"] });
      queryClient.invalidateQueries({ queryKey: ["producao-usuarios"] });
      queryClient.invalidateQueries({ queryKey: ["usuarios-producao"] });
      setIsRodizioSalgadoTimerActive(false);
      setRodizioSalgadoTamanhoId(null);
      setRodizioSalgadoLabel("");
      setRodizioSalgadoTimeElapsed(0);
    },
    onError: (error: any) => {
      Swal.fire(
        "Erro",
        error?.message || "Erro ao finalizar rodízio salgado",
        "error"
      );
    },
  });

  const finalizarSalaoMutation = useMutation({
    mutationFn: async (payload: {
      tamanhoId: number;
      bordaId: number;
      tempoTotal: number;
    }) => {
      return filaPizzasService.finalizarSalao(payload);
    },
    onSuccess: () => {
      toast.success("Salão finalizado e contabilizado com sucesso.");
      queryClient.invalidateQueries({ queryKey: ["ranking-dia-producao"] });
      queryClient.invalidateQueries({ queryKey: ["producoes"] });
      queryClient.invalidateQueries({ queryKey: ["producao-resumo"] });
      queryClient.invalidateQueries({ queryKey: ["producao-usuarios"] });
      queryClient.invalidateQueries({ queryKey: ["usuarios-producao"] });
      setIsSalaoTimerActive(false);
      setSalaoTamanhoId(null);
      setSalaoBordaId(null);
      setSalaoLabel("");
      setSalaoTimeElapsed(0);
    },
    onError: (error: any) => {
      Swal.fire("Erro", error?.message || "Erro ao finalizar salão", "error");
    },
  });

  const handleToggleOnline = useCallback(() => {
    toggleOnlineMutation.mutate();
  }, [toggleOnlineMutation]);

  const handleFinalizarProximo = useCallback(() => {
    void Swal.fire({
      title: "Concluir montagem?",
      text: "Confirma que esta pizza foi montada e deseja receber o próximo pedido?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: COLORS.SUCCESS,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, finalizar e próximo",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        finalizarProximoMutation.mutate();
      }
    });
  }, [finalizarProximoMutation]);

  const handleFinalizarOffline = useCallback(() => {
    void Swal.fire({
      title: "Concluir montagem e parar?",
      text: "Confirma que esta pizza foi montada e deseja ficar offline (parar de receber pedidos)?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: COLORS.ORANGE,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, finalizar e parar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        finalizarOfflineMutation.mutate();
      }
    });
  }, [finalizarOfflineMutation]);

  const openRodizioModal = useCallback(() => {
    setIsRodizioModalOpen(true);
  }, []);

  const closeRodizioModal = useCallback(() => {
    setIsRodizioModalOpen(false);
  }, []);

  const openSalaoModal = useCallback(() => {
    setIsSalaoModalOpen(true);
  }, []);

  const closeSalaoModal = useCallback(() => {
    setIsSalaoModalOpen(false);
  }, []);

  const handleCriarRodizio = useCallback(
    (tamanhoId: number, quantidade: number) => {
      criarRodizioMutation.mutate({ tamanhoId, quantidade });
    },
    [criarRodizioMutation]
  );

  const handleStartRodizioSalgadoTimer = useCallback(
    (tamanhoId: number, tamanhoNome: string) => {
      setRodizioSalgadoTamanhoId(tamanhoId);
      setRodizioSalgadoLabel(tamanhoNome);
      setRodizioSalgadoTimeElapsed(0);
      setIsRodizioSalgadoTimerActive(true);
      setIsRodizioModalOpen(false);
      toast.info(`Timer iniciado para ${tamanhoNome}.`);
    },
    []
  );

  useEffect(() => {
    if (!isRodizioSalgadoTimerActive) return;

    const interval = setInterval(() => {
      setRodizioSalgadoTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRodizioSalgadoTimerActive]);

  useEffect(() => {
    if (!isSalaoTimerActive) return;

    const interval = setInterval(() => {
      setSalaoTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isSalaoTimerActive]);

  const handleFinalizarRodizioSalgadoTimer = useCallback(() => {
    if (!rodizioSalgadoTamanhoId) {
      Swal.fire("Erro", "Tamanho do rodízio salgado não encontrado.", "error");
      return;
    }

    if (rodizioSalgadoTimeElapsed < 10) {
      toast.info("Produção de rodízio salgado cancelada (tempo muito curto).");
      setIsRodizioSalgadoTimerActive(false);
      setRodizioSalgadoTamanhoId(null);
      setRodizioSalgadoLabel("");
      setRodizioSalgadoTimeElapsed(0);
      return;
    }

    finalizarRodizioSalgadoMutation.mutate({
      tamanhoId: rodizioSalgadoTamanhoId,
      tempoTotal: rodizioSalgadoTimeElapsed,
    });
  }, [
    rodizioSalgadoTamanhoId,
    rodizioSalgadoTimeElapsed,
    finalizarRodizioSalgadoMutation,
  ]);

  const handleStartSalaoTimer = useCallback(
    (tamanhoId: number, tamanhoNome: string, bordaId: number) => {
      setSalaoTamanhoId(tamanhoId);
      setSalaoBordaId(bordaId);
      setSalaoLabel(tamanhoNome);
      setSalaoTimeElapsed(0);
      setIsSalaoTimerActive(true);
      setIsSalaoModalOpen(false);
      toast.info(`Timer iniciado para ${tamanhoNome}.`);
    },
    []
  );

  const handleFinalizarSalaoTimer = useCallback(() => {
    if (!salaoTamanhoId || !salaoBordaId) {
      Swal.fire("Erro", "Dados do salão não encontrados.", "error");
      return;
    }

    if (salaoTimeElapsed < 10) {
      toast.info("Produção de salão cancelada (tempo muito curto).");
      setIsSalaoTimerActive(false);
      setSalaoTamanhoId(null);
      setSalaoBordaId(null);
      setSalaoLabel("");
      setSalaoTimeElapsed(0);
      return;
    }

    finalizarSalaoMutation.mutate({
      tamanhoId: salaoTamanhoId,
      bordaId: salaoBordaId,
      tempoTotal: salaoTimeElapsed,
    });
  }, [salaoTamanhoId, salaoBordaId, salaoTimeElapsed, finalizarSalaoMutation]);

  return (
    <PizzaioloIntegracaoContext.Provider
      value={{
        isOnline,
        isFilaSalgada,
        isFilaDoce,
        isFilaSalaoSalgada,
        isFilaSalaoDoce,
        isFilaRodizioSalgada,
        isFilaRodizioDoce,
        currentItem,
        timeElapsed,
        isFinalizing:
          finalizarProximoMutation.isPending ||
          finalizarOfflineMutation.isPending,
        isPulando: pularItemMutation.isPending,
        isTogglingOnline: toggleOnlineMutation.isPending,
        isLoadingStatus,
        pendingSalgada,
        pendingDoce,
        pendingSalaoSalgada,
        pendingSalaoDoce,
        isRodizioModalOpen,
        isSalaoModalOpen,
        tamanhosRodizio,
        tamanhosSalao,
        bordasSalao,
        isLoadingTamanhosRodizio,
        isLoadingSalaoOptions: isLoadingTamanhosRodizio || isLoadingBordasSalao,
        isCreatingRodizio: criarRodizioMutation.isPending,
        isRodizioSalgadoTimerActive,
        rodizioSalgadoLabel,
        rodizioSalgadoTimeElapsed,
        isFinalizingRodizioSalgado: finalizarRodizioSalgadoMutation.isPending,
        isSalaoTimerActive,
        salaoLabel,
        salaoTimeElapsed,
        isFinalizingSalao: finalizarSalaoMutation.isPending,
        handleToggleOnline,
        handleFinalizarProximo,
        handleFinalizarOffline,
        openRodizioModal,
        closeRodizioModal,
        openSalaoModal,
        closeSalaoModal,
        handleCriarRodizio,
        handleStartRodizioSalgadoTimer,
        handleFinalizarRodizioSalgadoTimer,
        handleStartSalaoTimer,
        handleFinalizarSalaoTimer,
        handlePularItem: (motivo: string) => pularItemMutation.mutate(motivo),
        handleDefinirTamanhoRodizio: (tamanho: "08" | "broto") =>
          definirTamanhoRodizioMutation.mutate(tamanho),
        isDefinindoTamanhoRodizio: definirTamanhoRodizioMutation.isPending,
      }}
    >
      {children}
    </PizzaioloIntegracaoContext.Provider>
  );
};

export const usePizzaioloIntegracao = () => {
  const context = useContext(PizzaioloIntegracaoContext);
  if (!context) {
    throw new Error(
      "usePizzaioloIntegracao must be used within PizzaioloIntegracaoProvider"
    );
  }
  return context;
};
